export interface OperationFailure {
  message: string;
  code?: string;
  retryable: boolean;
}

const PERMANENT_CODES = new Set([
  "invalid_json",
  "invalid_multipart",
  "missing_file",
  "unsupported_file_type",
  "file_too_large",
  "source_text_too_short",
  "source_text_too_long",
  "invalid_source_url",
  "total_upload_limit_reached",
  "upload_limit_exceeded",
  "source_type_mismatch",
  "epub_parse_failed",
  "missing_required_fields",
  "invalid_target_level",
  "section_not_found",
  "daily_simplification_limit_exceeded",
]);

const PERMANENT_HTTP_STATUSES = new Set([
  400,
  401,
  403,
  404,
  405,
  413,
  422,
  429,
]);

interface ResponseLike {
  status?: unknown;
  clone?: () => { json?: () => Promise<unknown> };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export async function normalizeOperationFailure(
  error: unknown,
  fallback: string,
): Promise<OperationFailure> {
  const errorRecord = isRecord(error) ? error : undefined;
  const context = errorRecord?.context as ResponseLike | undefined;
  const status = typeof context?.status === "number" ? context.status : undefined;
  let body: Record<string, unknown> | undefined;

  try {
    const response = context?.clone?.();
    const parsed = await response?.json?.();
    body = isRecord(parsed) ? parsed : undefined;
  } catch {
    // A response body may have already been consumed or may not contain JSON.
  }

  const message =
    (typeof body?.message === "string" && body.message) ||
    (error instanceof Error && error.message) ||
    fallback;
  const code = typeof body?.code === "string" ? body.code : undefined;

  return {
    message,
    ...(code ? { code } : {}),
    retryable:
      !PERMANENT_CODES.has(code ?? "") &&
      !PERMANENT_HTTP_STATUSES.has(status ?? 0),
  };
}
